import { useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-32 pb-20 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-background-100 flex items-center justify-center mb-4">
              <i className="ri-file-unknow-line text-2xl text-foreground-300"></i>
            </div>
            <h1 className="text-xl font-heading font-semibold text-foreground-950 mb-2">文章未找到</h1>
            <p className="text-foreground-500 text-sm mb-6">抱歉，您访问的文章不存在或已被移除。</p>
            <Link
              to="/articles"
              className="inline-flex px-5 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors"
            >
              返回文章列表
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.categorySlug === article.categorySlug)
    .slice(0, 3);

  return (
    <>
      <Navbar />
      <main>
        <article className="pt-28 pb-10 md:pt-32 md:pb-14 bg-background-50">
          <div className="w-full max-w-[800px] mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-6 flex-wrap">
              <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link to="/articles" className="hover:text-primary-600 transition-colors">文章资讯</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link
                to={`/articles/${article.categorySlug}`}
                className="hover:text-primary-600 transition-colors"
              >
                {article.category}
              </Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700 truncate max-w-[200px]">{article.title}</span>
            </nav>

            <div className="mb-6">
              <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
                {article.category}
              </span>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-400">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i> {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i> {article.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i> {article.readTime}
                </span>
              </div>
            </div>

            <div className="rounded-2xl overflow-hidden mb-10">
              <img
                src={article.image}
                alt={article.title}
                className="w-full aspect-[16/9] object-cover"
              />
            </div>
          </div>
        </article>

        <section className="py-10 md:py-16 bg-background-50">
          <div className="w-full max-w-[800px] mx-auto px-4 md:px-6">
            <div
              className="prose prose-lg max-w-none prose-headings:font-heading prose-headings:text-foreground-950 prose-p:text-foreground-700 prose-p:leading-relaxed prose-strong:text-foreground-900 prose-h3:text-xl prose-h3:font-bold prose-h3:mt-8 prose-h3:mb-4"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-8 border-t border-background-200">
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs font-medium"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {relatedArticles.length > 0 && (
          <section className="py-14 md:py-20 bg-background-100">
            <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.slug}`}
                    className="group rounded-2xl bg-background-50 border border-background-200/70 overflow-hidden transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={ra.image}
                        alt={ra.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <span className="text-xs text-primary-600 font-medium">{ra.category}</span>
                      <h3 className="text-sm font-heading font-semibold text-foreground-950 mt-1 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {ra.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}